// ============================================
// PAGE - Inventario
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useProductosVendibles, useAllProductosVendibles, useDeleteProductoAlmacen, useProductosDestacados, useUpdateProductoAlmacen, useInventarioValorizado, useUIStore, getFechaHoyLocal, type ProductoVendible, productoRepository, useSession} from '@almacenes/core';
import { CategoriaAlmacenSelector } from '@/components/CategoriaAlmacenSelector';
import { Button } from '@almacenes/ui';
import { AddStockModal } from '@/components/AddStockModal';
import { BuscarImagenModal } from '@/components/BuscarImagenModal';
import { useBarcodeScan } from '@/hooks/useBarcodeScan';
import ExcelJS from 'exceljs';
import { 
  MdSearch, 
  MdAdd, 
  MdEdit, 
  MdDelete,
  MdFileDownload,
  MdAssessment,
  MdArrowUpward,
  MdArrowDownward,
  MdQrCode2,
  MdScale
} from 'react-icons/md';
import { FaBox, FaWhatsapp, FaCrown } from 'react-icons/fa6';
import productosPlaceholder from '@/assets/images/imagen_no_disponible.png';
import { goToPremium } from '@/lib/premium';

type SortField = 'productoNombre' | 'stock' | 'categoria' | null;
type SortDirection = 'asc' | 'desc';

interface DeleteConfirmation {
  productoAlmacenId: number;
  productoNombre: string;
}

type ProductosDestacadosTab = 'top10' | 'stockBajo';

// Alertas de inventario (Mi Negocio Pro) -> filtro ?alerta=... en /inventario
const ALERTA_LABELS: Record<string, string> = {
  critico: 'Stock crítico',
  bajo: 'Bajo stock',
  'sin-costo': 'Sin costo',
  'sin-codigo': 'Sin código de barras',
  'sin-categoria': 'Sin categoría',
};

export const InventarioPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const alertaFiltro = searchParams.get('alerta');
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation | null>(null);
  const [showDestacadosModal, setShowDestacadosModal] = useState(false);
  const [destacadosTab, setDestacadosTab] = useState<ProductosDestacadosTab>('top10');
  const [stockBajoPage, setStockBajoPage] = useState(0);
  const [showUpdateStockModal, setShowUpdateStockModal] = useState(false);
  const [selectedProducto, setSelectedProducto] = useState<ProductoVendible | null>(null);
  const [isLoadingProductoData, setIsLoadingProductoData] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [mostrarModalImagen, setMostrarModalImagen] = useState(false);
  const [productoSeleccionadoParaImagen, setProductoSeleccionadoParaImagen] = useState<ProductoVendible | null>(null);
  const [showInventarioValorizado, setShowInventarioValorizado] = useState(false);
  const [inventarioValorizadoPage, setInventarioValorizadoPage] = useState(0);
  const [categoriaFiltro, setCategoriaFiltro] = useState<number | null>(null);
  const [soloPacks, setSoloPacks] = useState(false);
  const pageSize = 20;
  const stockBajoPageSize = 10;

  // Construir el parámetro de ordenamiento (categoria se ordena client-side)
  const sortParam = sortField && sortField !== 'categoria' ? `${sortField},${sortDirection}` : undefined;

  const { data: session } = useSession();
  const hasPremiumAccess = session?.hasPremiumAccess || false;

  const { data, isLoading, isError, isFetching, refetch } = useProductosVendibles(
    currentPage,
    pageSize,
    searchTerm,
    sortParam,
    alertaFiltro || soloPacks ? undefined : categoriaFiltro ?? undefined,
    soloPacks ? undefined : alertaFiltro ?? undefined,
    soloPacks
  );

  // Si llega/cambia el filtro de alerta por query param (?alerta=...), volver a la primera página
  useEffect(() => {
    setCurrentPage(0);
  }, [alertaFiltro]);

  const deleteProductoMutation = useDeleteProductoAlmacen();
  const updateProductoMutation = useUpdateProductoAlmacen();
  const addToast = useUIStore((state) => state.addToast);
  // Solo cargar productos destacados cuando el modal esté abierto
  const { data: productosDestacados, isLoading: isLoadingDestacados, refetch: refetchProductosDestacados } = useProductosDestacados(showDestacadosModal);
  const { refetch: fetchAllProducts } = useAllProductosVendibles(searchTerm);
  // Cargar inventario valorizado solo cuando el modal esté abierto
  const { data: inventarioValorizado, isLoading: isLoadingInventarioValorizado } = useInventarioValorizado(inventarioValorizadoPage, 20, showInventarioValorizado);

  const handleSearch = () => {
    if (searchInput.length === 0 || searchInput.length >= 2) {
      setSearchTerm(searchInput);
      setCurrentPage(0);
    }
  };

  // Hook para detectar escaneos de código de barras de forma global
  useBarcodeScan({
    onScan: (barcode) => {      

      // Poner el código en el input de búsqueda
      setSearchInput(barcode);
      setSearchTerm(barcode);
      setCurrentPage(0);
      
    },
    enabled: true,
    minLength: 2,
  });

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Limpiar búsqueda si tiene menos de 2 caracteres
  useEffect(() => {
    if (searchInput.length > 0 && searchInput.length < 2) {
      // No hacer nada, esperar a que tenga 2 caracteres
    } else if (searchInput.length === 0 && searchTerm !== '') {
      setSearchTerm('');
      setCurrentPage(0);
    }
  }, [searchInput, searchTerm]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Si ya está ordenando por este campo, cambiar dirección
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Si es un campo nuevo, ordenar ascendente
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(0);
  };

  const getCategoriaNombre = (producto: ProductoVendible) =>
    producto.categoriaAlmacen?.nombre ?? producto.categoriaGlobal?.nombre ?? '';

  const contentOrdenado = (() => {
    const base = data?.content ?? [];
    if (sortField !== 'categoria') return base;
    return [...base].sort((a, b) => {
      const cmp = getCategoriaNombre(a).localeCompare(getCategoriaNombre(b), 'es');
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  })();

  const goToFirstPage = () => setCurrentPage(0);
  const goToPreviousPage = () => setCurrentPage(Math.max(0, currentPage - 1));
  const goToNextPage = () => {
    if (data && currentPage < data.totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };
  const goToLastPage = () => {
    if (data) {
      setCurrentPage(data.totalPages - 1);
    }
  };

  const formatPrice = (price: number | null) => {
    if (price === null) return '-';
    return `$${price.toLocaleString('es-CL')}`;
  };

  const formatStock = (stock: number, venderPor: string, esPack: boolean) => {
    if (esPack) {
      return '-';
    }
    if (venderPor === 'F') {
      return (
        <span className="inline-flex items-center gap-1">
          {stock.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}
          <MdScale 
            size={20}
            className="text-amber-600" 
            title="Producto pesable"
          />
        </span>
      );
    }
    return stock.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <MdArrowUpward className="text-gray-400 opacity-0 group-hover:opacity-50" />;
    }
    return sortDirection === 'asc' ? (
      <MdArrowUpward className="text-blue-600" />
    ) : (
      <MdArrowDownward className="text-blue-600" />
    );
  };

  const handleDeleteClick = (productoAlmacenId: number, productoNombre: string) => {
    setDeleteConfirmation({ productoAlmacenId, productoNombre });
  };

  const handleEdit = (productoAlmacenId: number, esPack: boolean) => {
    if (esPack) {
      navigate(`/inventario/editar-pack/${productoAlmacenId}`);
    } else {
      navigate(`/inventario/editar/${productoAlmacenId}`);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmation) return;

    try {
      await deleteProductoMutation.mutateAsync(deleteConfirmation.productoAlmacenId);
      addToast('success', `Producto "${deleteConfirmation.productoNombre}" eliminado exitosamente`);
      setDeleteConfirmation(null);
    } catch (error) {
      console.error('Error al eliminar producto:', error);
      addToast('error', 'Error al eliminar el producto. Por favor, intenta nuevamente.');
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation(null);
  };

  const handleUpdateStockClick = (producto: ProductoVendible) => {
    setSelectedProducto(producto);
    setShowUpdateStockModal(true);
  };

  const handleUpdateStockFromDestacados = async (productoAlmacenId: number) => {
    try {
      console.log('Obteniendo datos para productoAlmacenId:', productoAlmacenId);
      setIsLoadingProductoData(true);
      const productoCompleto = await productoRepository.getProductoVendibleById(productoAlmacenId);
      setSelectedProducto(productoCompleto);
      setShowUpdateStockModal(true);
    } catch (error: any) {
      console.error('Error al obtener datos del producto:', error);
      const mensajeError = error?.message || error?.response?.data?.message || 'Error al cargar los datos del producto';
      addToast('error', mensajeError);
    } finally {
      setIsLoadingProductoData(false);
    }
  };

  const handleCloseUpdateStockModal = () => {
    setShowUpdateStockModal(false);
    setSelectedProducto(null);
  };

  const handleClickImagen = (producto: ProductoVendible) => {
    setProductoSeleccionadoParaImagen(producto);
    setMostrarModalImagen(true);
  };

  const handleSeleccionarImagen = async (_imageUrl: string, imagenProductoId: number) => {
    if (!productoSeleccionadoParaImagen) return;

    try {
      console.log('Actualizando imagen para productoAlmacenId:', productoSeleccionadoParaImagen.productoAlmacenId, 'con imagenProductoId:', imagenProductoId);
      await updateProductoMutation.mutateAsync({
        productoAlmacenId: productoSeleccionadoParaImagen.productoAlmacenId,
        dto: { imagenProductoId },
      });

      addToast('success', 'Imagen actualizada exitosamente');
      setMostrarModalImagen(false);
      setProductoSeleccionadoParaImagen(null);
    } catch (error) {
      console.error('Error al actualizar imagen:', error);
      addToast('error', 'Error al actualizar la imagen');
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setIsDownloadingExcel(true);

      const result = await fetchAllProducts();

      if (!result.data || result.data.length === 0) {
        addToast('warning', 'No hay productos para exportar');
        return;
      }

      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Almacenes Digitales';
      const sheet = workbook.addWorksheet('Inventario', {
        views: [{ state: 'frozen', ySplit: 2 }],
      });

      // Fila de título
      sheet.mergeCells('A1:F1');
      const titleCell = sheet.getCell('A1');
      titleCell.value = searchTerm ? `Inventario — "${searchTerm}"` : 'Inventario completo';
      titleCell.font = { name: 'Calibri', size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF11194F' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      sheet.getRow(1).height = 32;

      // Cabeceras
      sheet.columns = [
        { key: 'codigo',        width: 22 },
        { key: 'nombre',        width: 44 },
        { key: 'formato',       width: 16 },
        { key: 'precioCompra',  width: 20 },
        { key: 'precioVenta',   width: 20 },
        { key: 'stock',         width: 14 },
      ];

      const headerRow = sheet.addRow(['Código de Barra', 'Nombre', 'Formato Venta', 'Precio de Compra', 'Precio de Venta', 'Stock']);
      headerRow.eachCell((cell) => {
        cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FF11194F' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF43BFFF' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
          bottom: { style: 'medium', color: { argb: 'FF11194F' } },
        };
      });
      headerRow.height = 28;

      // Filas de datos
      result.data.forEach((producto, index) => {
        const isEven = index % 2 === 0;
        const formatoVenta = producto.esPack ? 'Pack' : producto.venderPor === 'F' ? 'Granel' : 'Unidad';
        const row = sheet.addRow([
          producto.codigo,
          producto.productoNombre,
          formatoVenta,
          producto.precioCompraActual ?? 0,
          producto.precioVentaActual ?? 0,
          producto.esPack ? '-' : producto.stock,
        ]);

        const rowBg = isEven ? 'FFFFFFFF' : 'FFF3F4F9';

        row.eachCell((cell, colNumber) => {
          cell.font = { name: 'Calibri', size: 10 };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } };
          cell.alignment = { vertical: 'middle', horizontal: colNumber === 2 ? 'left' : 'center' };
          cell.border = {
            bottom: { style: 'hair', color: { argb: 'FFD1D5DB' } },
          };

          // Formato moneda para precios
          if (colNumber === 4 || colNumber === 5) {
            cell.numFmt = '"$"#,##0';
          }
        });

        row.height = 22;
      });

      // Fila de total al final
      const totalRow = sheet.addRow(['', `Total: ${result.data.length} productos`, '', '', '', '']);
      totalRow.getCell(2).font = { name: 'Calibri', size: 10, bold: true, italic: true, color: { argb: 'FF6B7280' } };
      totalRow.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };
      totalRow.height = 20;

      const fecha = getFechaHoyLocal();
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventario_${fecha}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);

      addToast('success', 'Inventario descargado exitosamente');
    } catch (error) {
      console.error('Error al descargar inventario:', error);
      addToast('error', 'Error al generar el archivo de Excel');
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  // Filtrar productos con stock bajo y paginar
  const productosStockBajo = productosDestacados?.filter(p => p.isLowStock) || [];
  const totalStockBajoPages = Math.ceil(productosStockBajo.length / stockBajoPageSize);
  const stockBajoPaginado = productosStockBajo.slice(
    stockBajoPage * stockBajoPageSize,
    (stockBajoPage + 1) * stockBajoPageSize
  );

  return (
    <div className="min-h-screen">
      <div className="px-4 md:px-6 lg:px-8 py-6">
        {/* Título */}
        <h1 className="text-4xl font-bold mb-6" style={{ color: '#11194F' }}>
          Inventario
        </h1>

        {/* Barra de búsqueda */}
        <div className="mb-6 flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Buscar producto por nombre o código (mínimo 2 caracteres)..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full px-6 py-4 text-lg border-2 border-gray-300 rounded-xl focus:outline-none focus:border-primary transition-colors"
              style={{ borderColor: '#E5E7EB' }}
            />
            {searchInput.length > 0 && searchInput.length < 2 && (
              <p className="absolute left-6 -bottom-5 text-xs text-orange-600">
                Ingresa al menos 2 caracteres para buscar
              </p>
            )}
          </div>
          <button
            onClick={handleSearch}
            disabled={searchInput.length > 0 && searchInput.length < 2}
            className="px-8 rounded-xl flex items-center justify-center transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#FACC15' }}
          >
            <MdSearch className="text-3xl" style={{ color: '#11194F' }} />
          </button>
        </div>

        {/* Filtro de alerta de inventario (viene de Mi Negocio Pro vía ?alerta=...) */}
        {alertaFiltro && (
          <div className="mb-6">
            <span
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold"
              style={{ backgroundColor: '#FEF2F2', color: '#B91C1C' }}
            >
              Filtrado por: {ALERTA_LABELS[alertaFiltro] ?? alertaFiltro}
              <button
                type="button"
                onClick={() => navigate('/inventario')}
                className="font-bold hover:opacity-70"
                aria-label="Quitar filtro"
              >
                &times;
              </button>
            </span>
          </div>
        )}

        {/* Filtro "Solo packs" (no se combina con el filtro de alerta ni con el de categoría) */}
        {!alertaFiltro && (
          <div className="mb-6">
            <button
              type="button"
              aria-pressed={soloPacks}
              onClick={() => { setSoloPacks((v) => !v); setCurrentPage(0); }}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
                soloPacks
                  ? 'bg-purple-100 text-purple-700 border-purple-300'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <FaBox className="text-base" />
              Solo packs
              {soloPacks && <span className="font-bold" aria-hidden="true">&times;</span>}
            </button>
          </div>
        )}

        {/* Filtro por categoría (solo Premium, oculto mientras hay un filtro de alerta o de packs activo) */}
        {hasPremiumAccess && !alertaFiltro && !soloPacks && (
          <div className="mb-6">
            <CategoriaAlmacenSelector
              value={categoriaFiltro}
              onChange={(id) => { setCategoriaFiltro(id); setCurrentPage(0); }}
            />
          </div>
        )}

        {/* Botones de acción */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate('/inventario/ingresar')}
            className="w-full shadow-md hover:shadow-lg"
          >
            <div className="flex items-center justify-center gap-2">
              <MdAdd className="text-2xl flex-shrink-0" />
              <span>Ingresar Producto</span>
            </div>
          </Button>

          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate('/inventario/crear-pack')}
            className="w-full shadow-md hover:shadow-lg"
          >
            <div className="flex items-center justify-center gap-2">
              <FaBox className="text-xl flex-shrink-0" />
              <span>Ingresar Packs</span>
            </div>
          </Button>

          <Button
            variant="primary"
            size="lg"
            onClick={handleDownloadExcel}
            disabled={isDownloadingExcel}
            className="w-full shadow-md hover:shadow-lg"
          >
            <div className="flex items-center justify-center gap-2">
              <MdFileDownload className="text-2xl flex-shrink-0" />
              <span>Descarga Inventario</span>
            </div>
          </Button>

          {/* TODO: Se evalua si dejamos esta sección de productos destacados o la movemos a otra parte, ya que no es algo que se consulte tan seguido y podría no ser tan relevante para el usuario promedio. Por ahora se deja comentada para no eliminar el código.
          <Button
            variant="primary"
            size="lg"
            onClick={() => setShowDestacadosModal(true)}
            className="w-full shadow-md hover:shadow-lg"
          >
            <div className="flex items-center justify-center gap-2">
              <MdStar className="text-2xl flex-shrink-0" />
              <span>Productos Destacados</span>
            </div>
          </Button>
          */}

          <Button
            variant="primary"
            size="lg"
            onClick={() => setShowInventarioValorizado(true)}
            className="w-full shadow-md hover:shadow-lg"
          >
            <div className="flex items-center justify-center gap-2">
              <MdAssessment className="text-2xl flex-shrink-0" />
              <span>Inventario Valorizado</span>
            </div>
          </Button>

          <Button
            variant="highlight"
            size="lg"
            onClick={() => window.open('https://api.whatsapp.com/send?phone=56920201644&text=Hola,%20necesito%20ayuda%20para%20hacer%20una%20carga%20masiva%20de%20mi%20inventario', '_blank')}
            className="w-full shadow-md hover:shadow-lg"
          >
            <div className="flex items-center justify-center gap-2">
              <FaWhatsapp className="text-2xl flex-shrink-0" />
              <span>Carga Masiva</span>
            </div>
          </Button>

          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate('/inventario/etiquetas-granel')}
            className="w-full shadow-md hover:shadow-lg"
          >
            <div className="flex items-center justify-center gap-2">
              <MdQrCode2 className="text-2xl flex-shrink-0" />
              <span>Etiquetas Granel</span>
            </div>
          </Button>
          
        </div>

        {/* Paginación Superior */}
        {data && (
          <div className="mb-6 flex justify-center items-center gap-2">
            <button
              onClick={goToFirstPage}
              disabled={currentPage === 0}
              className="p-2 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
            </button>

            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 0}
              className="p-2 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>

            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, data.totalPages) }, (_, i) => {
                let pageNum = i;
                if (data.totalPages > 5) {
                  if (currentPage < 3) {
                    pageNum = i;
                  } else if (currentPage > data.totalPages - 4) {
                    pageNum = data.totalPages - 5 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-4 py-2 rounded font-medium transition-colors ${
                      currentPage === pageNum
                        ? 'text-white'
                        : 'hover:bg-gray-200'
                    }`}
                    style={
                      currentPage === pageNum
                        ? { backgroundColor: '#FACC15', color: '#11194F' }
                        : { color: '#11194F' }
                    }
                  >
                    {pageNum + 1}
                  </button>
                );
              })}
            </div>

            <button
              onClick={goToNextPage}
              disabled={!data || currentPage >= data.totalPages - 1}
              className="p-2 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>

            <button
              onClick={goToLastPage}
              disabled={!data || currentPage >= data.totalPages - 1}
              className="p-2 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10.293 15.707a1 1 0 010-1.414L14.586 10l-4.293-4.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
                <path fillRule="evenodd" d="M4.293 15.707a1 1 0 010-1.414L8.586 10 4.293 5.707a1 1 0 011.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}

        {/* Tabla de productos */}
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead style={{ backgroundColor: '#F3F4F9' }}>
                <tr>
                  <th 
                    className="px-6 py-4 text-left text-sm font-bold cursor-pointer hover:bg-gray-200 transition-colors group"
                    style={{ color: '#11194F' }}
                    onClick={() => handleSort('productoNombre')}
                  >
                    <div className="flex items-center gap-2">
                      <span>Producto</span>
                      {renderSortIcon('productoNombre')}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                    Formato Venta
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                    Precio de Venta
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                    Precio de Compra
                  </th>                  
                  <th 
                    className="px-6 py-4 text-left text-sm font-bold cursor-pointer hover:bg-gray-200 transition-colors group"
                    style={{ color: '#11194F' }}
                    onClick={() => handleSort('stock')}
                  >
                    <div className="flex items-center gap-2">
                      <span>Stock</span>
                      {renderSortIcon('stock')}
                    </div>
                  </th>
                  <th
                    className={`px-6 py-4 text-left text-sm font-bold ${hasPremiumAccess ? 'cursor-pointer hover:bg-gray-200 transition-colors group' : ''}`}
                    style={{ color: '#11194F' }}
                    onClick={() => hasPremiumAccess && handleSort('categoria')}
                  >
                    <div className="flex items-center gap-1">
                      <span>Categoría</span>
                      {hasPremiumAccess ? renderSortIcon('categoria') : (
                        <button onClick={(e) => { e.stopPropagation(); goToPremium(); }} title="Función Premium" className="hover:opacity-70 transition-opacity">
                          <FaCrown className="text-purple-500 text-sm" />
                        </button>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-bold" style={{ color: '#11194F' }}>
                    Actualizar Stock
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-bold" style={{ color: '#11194F' }}>
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className={isFetching && !isLoading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent" style={{ borderTopColor: '#43BFFF', borderBottomColor: '#43BFFF', borderLeftColor: '#43BFFF' }} />
                      </div>
                      <p className="mt-4 text-gray-600">Cargando inventario...</p>
                    </td>
                  </tr>
                ) : isError ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <p className="text-gray-700">No pudimos cargar el inventario.</p>
                      <Button
                        variant="primary"
                        size="md"
                        onClick={() => refetch()}
                        className="mt-4"
                      >
                        Reintentar
                      </Button>
                    </td>
                  </tr>
                ) : data?.content.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-600">
                      No se encontraron productos
                    </td>
                  </tr>
                ) : (
                  contentOrdenado.map((producto, index) => (
                    <tr
                      key={producto.productoAlmacenId}
                      className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleClickImagen(producto)}
                            className="w-12 h-12 flex-shrink-0 rounded overflow-hidden hover:opacity-80 transition-opacity cursor-pointer border-2 border-transparent hover:border-primary"
                          >
                            {producto.imagenUrl ? (
                              <img
                                src={producto.imagenUrl}
                                alt={producto.productoNombre}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.src = productosPlaceholder;
                                }}
                              />
                            ) : (
                              <div className="w-full h-full bg-gray-100 flex items-center justify-center p-1">
                                <img 
                                  src={productosPlaceholder} 
                                  alt="Producto" 
                                  className="w-full h-full object-contain"
                                />
                              </div>
                            )}
                          </button>
                          <div>
                            <p className="font-semibold" style={{ color: '#11194F' }}>
                              {producto.productoNombre}
                            </p>
                            <p className="text-sm text-gray-500">{producto.codigo}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm" style={{ color: '#11194F' }}>
                          {producto.esPack 
                            ? 'Pack' 
                            : producto.venderPor === 'U' 
                            ? 'Unidad' 
                            : 'Granel'
                          }
                        </span>
                      </td>                      
                      <td className="px-6 py-4">
                        <span className="font-semibold" style={{ color: '#11194F' }}>
                          {formatPrice(producto.precioVentaActual)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-semibold" style={{ color: '#11194F' }}>
                          {formatPrice(producto.precioCompraActual)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`font-semibold ${
                            producto.esPack
                              ? 'text-gray-500'
                              : producto.stock < 0
                              ? 'text-red-600'
                              : producto.stock === 0
                              ? 'text-orange-600'
                              : 'text-green-600'
                          }`}
                        >
                          {formatStock(producto.stock, producto.venderPor, producto.esPack)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {hasPremiumAccess ? (
                          producto.categoriaAlmacen ? (
                            <span className="inline-block px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                              {producto.categoriaAlmacen.nombre}
                            </span>
                          ) : producto.categoriaGlobal ? (
                            <span className="inline-block px-2 py-1 bg-gray-100 text-gray-500 rounded-full text-xs" title="Categoría del sistema">
                              {producto.categoriaGlobal.nombre}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">Sin categoría</span>
                          )
                        ) : producto.categoriaGlobal ? (
                          <span className="inline-block px-2 py-1 bg-gray-100 text-gray-500 rounded-full text-xs" title="Categoría del sistema">
                            {producto.categoriaGlobal.nombre}
                          </span>
                        ) : (
                          <button onClick={() => goToPremium()} className="flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full hover:opacity-80 transition-opacity">
                            <FaCrown className="text-xs text-white" />
                            <span className="text-xs font-bold text-white">Premium</span>
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {!producto.esPack && (
                          <button
                            onClick={() => handleUpdateStockClick(producto)}
                            className="inline-flex items-center justify-center w-10 h-10 rounded-lg border-2 transition-all hover:scale-110"
                            style={{ 
                              borderColor: '#43BFFF',
                              backgroundColor: '#EAF6FD'
                            }}
                          >
                            <MdAdd className="text-2xl" style={{ color: '#43BFFF' }} />
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleEdit(producto.productoAlmacenId, producto.esPack)}
                            className="p-2 rounded-lg transition-all hover:scale-110"
                            style={{ color: '#43BFFF' }}
                          >
                            <MdEdit className="text-xl" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(producto.productoAlmacenId, producto.productoNombre)}
                            className="p-2 rounded-lg transition-all hover:scale-110"
                            style={{ color: '#EF4444' }}
                          >
                            <MdDelete className="text-xl" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Info de paginación */}
        {data && (
          <div className="mt-4 text-center text-sm text-gray-600">
            Mostrando {data.content.length} de {data.totalElements} productos
          </div>
        )}
      </div>

      {/* Modal de confirmación de eliminación */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fadeIn">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-red-100">
              <MdDelete className="text-4xl text-red-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-center mb-2" style={{ color: '#11194F' }}>
              ¿Eliminar producto?
            </h2>
            
            <p className="text-center text-gray-600 mb-2">
              Estás a punto de eliminar el siguiente producto:
            </p>
            
            <p className="text-center font-semibold text-lg mb-6" style={{ color: '#11194F' }}>
              {deleteConfirmation.productoNombre}
            </p>
            
            <p className="text-center text-sm text-gray-500 mb-6">
              Esta acción no se puede deshacer.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={handleCancelDelete}
                disabled={deleteProductoMutation.isPending}
                className="flex-1 px-6 py-3 rounded-xl font-semibold transition-all hover:bg-gray-100 border-2 border-gray-300 disabled:opacity-50"
                style={{ color: '#11194F' }}
              >
                Cancelar
              </button>
              
              <button
                onClick={handleConfirmDelete}
                disabled={deleteProductoMutation.isPending}
                className="flex-1 px-6 py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#EF4444' }}
              >
                {deleteProductoMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    <span>Eliminando...</span>
                  </>
                ) : (
                  'Sí, eliminar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Productos Destacados */}
      {showDestacadosModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-fadeIn">
            {/* Header con botón cerrar */}
            <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: '#E5E7EB' }}>
              <h2 className="text-3xl font-bold" style={{ color: '#11194F' }}>
                Productos Destacados
              </h2>
              <button
                onClick={() => {
                  setShowDestacadosModal(false);
                  setDestacadosTab('top10');
                  setStockBajoPage(0);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b" style={{ borderColor: '#E5E7EB' }}>
              <button
                onClick={() => {
                  setDestacadosTab('top10');
                  setStockBajoPage(0);
                }}
                className={`flex-1 px-6 py-4 font-semibold transition-colors ${
                  destacadosTab === 'top10'
                    ? 'border-b-2'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                style={
                  destacadosTab === 'top10'
                    ? { borderColor: '#FACC15', backgroundColor: '#FFFBEB', color: '#11194F' }
                    : {}
                }
              >
                Top 10 Vendidos
              </button>
              <button
                onClick={() => {
                  setDestacadosTab('stockBajo');
                  setStockBajoPage(0);
                }}
                className={`flex-1 px-6 py-4 font-semibold transition-colors ${
                  destacadosTab === 'stockBajo'
                    ? 'border-b-2'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                style={
                  destacadosTab === 'stockBajo'
                    ? { borderColor: '#FACC15', backgroundColor: '#FFFBEB', color: '#11194F' }
                    : {}
                }
              >
                Stock Bajo
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 180px)' }}>
              {isLoadingDestacados ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent" style={{ borderTopColor: '#43BFFF', borderBottomColor: '#43BFFF', borderLeftColor: '#43BFFF' }} />
                </div>
              ) : destacadosTab === 'top10' ? (
                <>
                  <div className="mb-4 text-center">
                    <p className="text-xl mb-2">¡Atención! 😮</p>
                    <p className="text-gray-700">
                      Estos son los 10 productos que más has vendido en los últimos 60 días
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Esta información se actualiza una vez al día.
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead style={{ backgroundColor: '#F3F4F9' }}>
                        <tr>
                          <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                            Producto
                          </th>
                          <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                            Cantidad Vendida
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {productosDestacados?.slice(0, 10).map((producto, index) => (
                          <tr
                            key={index}
                            className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                          >
                            <td className="px-6 py-4">
                              <span className="font-medium" style={{ color: '#11194F' }}>
                                {producto.nombreProducto}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="font-semibold" style={{ color: '#11194F' }}>
                                {producto.cantidad}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-4 text-center">
                    <p className="text-xl mb-2">¡Cuidado! 😱</p>
                    <p className="text-gray-700">
                      Estos son parte de los productos que mas vendes y su stock está bajo, que no falten en tu inventario
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      El listado de productos se actualiza una vez al día; el stock se muestra en tiempo real.
                    </p>
                  </div>

                  {/* Paginación para Stock Bajo */}
                  {totalStockBajoPages > 1 && (
                    <div className="mb-4 flex justify-center items-center gap-2">
                      <button
                        onClick={() => setStockBajoPage(0)}
                        disabled={stockBajoPage === 0}
                        className="p-2 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                        </svg>
                      </button>

                      <button
                        onClick={() => setStockBajoPage(Math.max(0, stockBajoPage - 1))}
                        disabled={stockBajoPage === 0}
                        className="p-2 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>

                      <span
                        className="px-4 py-2 rounded font-medium"
                        style={{ backgroundColor: '#FACC15', color: '#11194F' }}
                      >
                        {stockBajoPage + 1}
                      </span>

                      <button
                        onClick={() => setStockBajoPage(Math.min(totalStockBajoPages - 1, stockBajoPage + 1))}
                        disabled={stockBajoPage >= totalStockBajoPages - 1}
                        className="p-2 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>

                      <button
                        onClick={() => setStockBajoPage(totalStockBajoPages - 1)}
                        disabled={stockBajoPage >= totalStockBajoPages - 1}
                        className="p-2 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10.293 15.707a1 1 0 010-1.414L14.586 10l-4.293-4.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          <path fillRule="evenodd" d="M4.293 15.707a1 1 0 010-1.414L8.586 10 4.293 5.707a1 1 0 011.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  )}

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead style={{ backgroundColor: '#F3F4F9' }}>
                        <tr>
                          <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                            Producto
                          </th>
                          <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                            Stock
                          </th>
                          <th className="px-6 py-4 text-center text-sm font-bold" style={{ color: '#11194F' }}>
                            Actualizar Stock
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockBajoPaginado.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="px-6 py-12 text-center text-gray-600">
                              No hay productos con stock bajo
                            </td>
                          </tr>
                        ) : (
                          stockBajoPaginado.map((producto, index) => (
                            <tr
                              key={index}
                              className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                            >
                              <td className="px-6 py-4">
                                <span className="font-medium" style={{ color: '#11194F' }}>
                                  {producto.nombreProducto}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span
                                  className={`font-semibold ${
                                    producto.stockActual < 0
                                      ? 'text-red-600'
                                      : producto.stockActual === 0
                                      ? 'text-orange-600'
                                      : 'text-gray-600'
                                  }`}
                                >
                                  {producto.stockActual}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-center">
                                <button
                                  onClick={() => handleUpdateStockFromDestacados(producto.productoAlmacenId)}
                                  disabled={isLoadingProductoData}
                                  className="inline-flex items-center justify-center px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                                  style={{ backgroundColor: '#43BFFF' }}
                                >
                                  {isLoadingProductoData ? (
                                    <>
                                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                                      <span>Cargando...</span>
                                    </>
                                  ) : (
                                    <>
                                      <MdAdd className="text-xl mr-1" />
                                      Actualizar
                                    </>
                                  )}
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            
          </div>
        </div>
      )}

      {/* Modal de Actualizar Stock */}
      {selectedProducto && (
        <AddStockModal
          isOpen={showUpdateStockModal}
          onClose={handleCloseUpdateStockModal}
          producto={selectedProducto}
          onSuccess={() => {
            refetchProductosDestacados();
          }}
        />
      )}

      {/* Overlay de descarga */}
      {isDownloadingExcel && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-solid border-current border-r-transparent mb-4" style={{ borderTopColor: '#43BFFF', borderBottomColor: '#43BFFF', borderLeftColor: '#43BFFF' }} />
              
              <h3 className="text-2xl font-bold mb-2" style={{ color: '#11194F' }}>
                Generando Excel...
              </h3>
              
              <p className="text-center text-gray-600">
                Por favor espera mientras se descarga tu inventario
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modal de búsqueda de imágenes */}
      <BuscarImagenModal
        isOpen={mostrarModalImagen}
        onClose={() => {
          setMostrarModalImagen(false);
          setProductoSeleccionadoParaImagen(null);
        }}
        onSelectImage={handleSeleccionarImagen}
        initialSearch={productoSeleccionadoParaImagen?.productoNombre || ''}
        productName={productoSeleccionadoParaImagen?.productoNombre}
      />

      {/* Modal de Inventario Valorizado */}
      {showInventarioValorizado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden animate-fadeIn">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: '#E5E7EB' }}>
              <h2 className="text-3xl font-bold" style={{ color: '#11194F' }}>
                Inventario Valorizado
              </h2>
              <button
                onClick={() => {
                  setShowInventarioValorizado(false);
                  setInventarioValorizadoPage(0);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 240px)' }}>
              {isLoadingInventarioValorizado ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent" style={{ borderTopColor: '#43BFFF', borderBottomColor: '#43BFFF', borderLeftColor: '#43BFFF' }} />
                </div>
              ) : inventarioValorizado ? (
                <>
                  {/* Resumen de totales */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
                      <p className="text-sm text-gray-600 mb-1">Total Precio de Compra</p>
                      <p className="text-3xl font-bold" style={{ color: '#43BFFF' }}>
                        ${inventarioValorizado.totalCompra.toLocaleString('es-CL')}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-6 border-2 border-green-200">
                      <p className="text-sm text-gray-600 mb-1">Total Precio de Venta</p>
                      <p className="text-3xl font-bold text-green-600">
                        ${inventarioValorizado.totalVenta.toLocaleString('es-CL')}
                      </p>
                    </div>
                  </div>

                  {/* Paginación Superior */}
                  {inventarioValorizado.details.totalPages > 1 && (
                    <div className="mb-4 flex justify-center items-center gap-2">
                      <button
                        onClick={() => setInventarioValorizadoPage(0)}
                        disabled={inventarioValorizadoPage === 0}
                        className="p-2 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                        </svg>
                      </button>

                      <button
                        onClick={() => setInventarioValorizadoPage(Math.max(0, inventarioValorizadoPage - 1))}
                        disabled={inventarioValorizadoPage === 0}
                        className="p-2 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>

                      <div className="flex gap-1">
                        {Array.from({ length: Math.min(5, inventarioValorizado.details.totalPages) }, (_, i) => {
                          let pageNum = i;
                          if (inventarioValorizado.details.totalPages > 5) {
                            if (inventarioValorizadoPage < 3) {
                              pageNum = i;
                            } else if (inventarioValorizadoPage > inventarioValorizado.details.totalPages - 4) {
                              pageNum = inventarioValorizado.details.totalPages - 5 + i;
                            } else {
                              pageNum = inventarioValorizadoPage - 2 + i;
                            }
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => setInventarioValorizadoPage(pageNum)}
                              className={`px-4 py-2 rounded font-medium transition-colors ${
                                inventarioValorizadoPage === pageNum
                                  ? 'text-white'
                                  : 'hover:bg-gray-200'
                              }`}
                              style={
                                inventarioValorizadoPage === pageNum
                                  ? { backgroundColor: '#FACC15', color: '#11194F' }
                                  : { color: '#11194F' }
                              }
                            >
                              {pageNum + 1}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={() => setInventarioValorizadoPage(Math.min(inventarioValorizado.details.totalPages - 1, inventarioValorizadoPage + 1))}
                        disabled={inventarioValorizadoPage >= inventarioValorizado.details.totalPages - 1}
                        className="p-2 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>

                      <button
                        onClick={() => setInventarioValorizadoPage(inventarioValorizado.details.totalPages - 1)}
                        disabled={inventarioValorizadoPage >= inventarioValorizado.details.totalPages - 1}
                        className="p-2 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10.293 15.707a1 1 0 010-1.414L14.586 10l-4.293-4.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          <path fillRule="evenodd" d="M4.293 15.707a1 1 0 010-1.414L8.586 10 4.293 5.707a1 1 0 011.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  )}

                  {/* Tabla de productos */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead style={{ backgroundColor: '#F3F4F9' }}>
                        <tr>
                          <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                            Producto
                          </th>
                          <th className="px-6 py-4 text-center text-sm font-bold" style={{ color: '#11194F' }}>
                            Stock
                          </th>
                          <th className="px-6 py-4 text-right text-sm font-bold" style={{ color: '#11194F' }}>
                            Precio Compra
                          </th>
                          <th className="px-6 py-4 text-right text-sm font-bold" style={{ color: '#11194F' }}>
                            Total Compra
                          </th>
                          <th className="px-6 py-4 text-right text-sm font-bold" style={{ color: '#11194F' }}>
                            Precio Venta
                          </th>
                          <th className="px-6 py-4 text-right text-sm font-bold" style={{ color: '#11194F' }}>
                            Total Venta
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventarioValorizado.details.content.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-6 py-12 text-center text-gray-600">
                              No hay productos en el inventario
                            </td>
                          </tr>
                        ) : (
                          inventarioValorizado.details.content.map((item, index) => (
                            <tr
                              key={index}
                              className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                            >
                              <td className="px-6 py-4">
                                <span className="font-medium" style={{ color: '#11194F' }}>
                                  {item.productoNombre}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-center">
                                <span
                                  className={`font-semibold ${
                                    item.stock < 0
                                      ? 'text-red-600'
                                      : item.stock === 0
                                      ? 'text-orange-600'
                                      : 'text-green-600'
                                  }`}
                                >
                                  {item.stock}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-right">
                                <span className="font-medium text-gray-700">
                                  ${item.precioCompra.toLocaleString('es-CL')}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-right">
                                <span className="font-semibold" style={{ color: '#43BFFF' }}>
                                  ${item.totalPrecioCompra.toLocaleString('es-CL')}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-right">
                                <span className="font-medium text-gray-700">
                                  ${item.precioVenta.toLocaleString('es-CL')}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-right">
                                <span className="font-semibold text-green-600">
                                  ${item.totalPrecioVenta.toLocaleString('es-CL')}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Info de paginación */}
                  <div className="mt-4 text-center text-sm text-gray-600">
                    Mostrando {inventarioValorizado.details.content.length} de {inventarioValorizado.details.totalElements} productos
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-gray-600">
                  No se pudo cargar el inventario valorizado
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
